import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { DialogStateManager } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { IconButton } from '@plato/ui/candidates/icon-button';
import { ToggleGroup } from '@plato/ui/containers/toggle-group';
import { Avatar } from '@plato/ui/primitives/avatar';
import { Button } from '@plato/ui/primitives/button';
import { InsetToggleButton } from '@plato/ui/primitives/inset-toggle-button';
import { ToolbarButton } from '@plato/ui/primitives/toolbar-button';
import { ValueChip, ValueChipBody, ValueChipLabel, ValueChipRemove } from '@plato/ui/primitives/value-chip';
import { cn } from '@plato/ui/util/classes';
import {
    addDays,
    formatISO,
    getWeek,
    intlFormat,
    isFriday,
    isMonday,
    isSameDay,
    isSameWeek,
    isWeekend,
    nextMonday,
    parseISO,
    previousFriday,
    subDays,
} from 'date-fns';
import {
    ArrowUpRightIcon,
    CalendarDaysIcon,
    CalendarIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    MapIcon,
    PlusIcon,
    UserCircleIcon,
    XIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import type { EventCreateDialogProps } from '../../events/components/event-create-dialog.tsx';
import { EventCreateDialog } from '../../events/components/event-create-dialog.tsx';
import type { ViewMode } from '../../events/components/events-view-mode-selector.tsx';
import { useDayRouteMapsUrl } from '../hooks/use-day-route-maps-url.ts';
import { getInitials } from '../utils/get-initials.ts';
import { CalendarDatePicker } from './calendar-date-picker.tsx';
import { ForeignCalendarPicker } from './foreign-calendar-picker.tsx';

export type Props = {
    day: string;
    viewMode: ViewMode;
    skipWeekends: boolean;
    className?: string;
    selectedOwnerId: string;
    onChangedDay: (day: string) => void;
    onViewModeChange: (mode: ViewMode) => void;
    onOwnerChange?: (ownerId: string | null) => void;
};

export function EventsOverviewHeaderNew({
    day,
    viewMode,
    skipWeekends,
    selectedOwnerId,
    onChangedDay,
    onViewModeChange,
    onOwnerChange,
}: Props): ReactNode {
    const { t } = useTranslation();
    const { userId } = useAppConfig();
    const locale = useResolvedLocale();
    const sendEvent = useAnalytics();

    const date = parseISO(day);

    const monthTitle = intlFormat(date, { month: 'long', year: 'numeric' }, { locale });
    const weekLabel = `${t('calendar_week_abbr', { ns: 'events' })}${getWeek(date, { weekStartsOn: 1, firstWeekContainsDate: 4 })}`;

    const step = viewMode === 'week' ? 7 : 1;
    // Without weekends, a Saturday or Sunday shows the Friday before it, so that is "today" then
    const now = new Date();
    const shownToday = skipWeekends && isWeekend(now) ? previousFriday(now) : now;
    const isCurrentWeek = isSameWeek(date, shownToday, { weekStartsOn: 1 });
    const isCurrentPeriod = viewMode === 'week' ? isCurrentWeek : isSameDay(date, shownToday);
    // Without weekends, from Friday to Monday and back
    const previousDate = skipWeekends && isMonday(date) ? previousFriday(date) : subDays(date, step);
    const nextDate = skipWeekends && isFriday(date) ? nextMonday(date) : addDays(date, step);

    const handleChange = (newDay: Date, direction: 'previous' | 'next' | 'today' | 'picker'): void => {
        sendEvent('event_calendar_period_changed', { direction, view_mode: viewMode });
        onChangedDay(formatISO(newDay, { representation: 'date' }));
    };
    const goToToday = (): void => {
        handleChange(new Date(), 'today');
    };
    const pickDate = (picked: Date): void => {
        handleChange(picked, 'picker');
    };

    const canCreateEvent = selectedOwnerId === userId;
    const openCreateEvent = (): void => {
        void DialogStateManager.show(EventCreateDialog, {
            date: day,
            analyticsSource: 'calendar',
        } satisfies EventCreateDialogProps);
    };

    const { mapsUrl, onOpen } = useDayRouteMapsUrl({
        day,
        ownerId: selectedOwnerId,
        enabled: viewMode === 'day',
    });
    const mapsLinkProps = mapsUrl
        ? { href: mapsUrl, target: '_blank', rel: 'noopener noreferrer', onClick: onOpen }
        : { disabled: true };

    // getSubordinates includes the signed-in user, so one entry means there is nobody else's calendar to show
    const { data: teamMembers = [] } = api.platoUser.query.users.getManyUsers.getSubordinates.useQuery();
    const canViewForeignCalendar = teamMembers.length > 1;
    const colleagues = teamMembers.filter((member) => member.id !== userId);
    const isForeignCalendar = selectedOwnerId !== userId;
    const shownUser = teamMembers.find((member) => member.id === selectedOwnerId);
    const shownUserName = shownUser ? `${shownUser.firstName} ${shownUser.lastName}` : '';

    const [isPickerOpen, setPickerOpen] = useState(false);
    const pickColleague = (colleagueId: string): void => {
        sendEvent('event_team_member_calendar_viewed', { selected_user_id: colleagueId });
        onOwnerChange?.(colleagueId);
    };
    const backToOwnCalendar = (): void => {
        onOwnerChange?.(null);
    };

    // one element, shown in the top row or the second row depending on the header width
    const foreignCalendarControl =
        canViewForeignCalendar &&
        (isForeignCalendar ? (
            // brand tint marks the whole page as showing someone else's calendar
            <ValueChip
                label={t('foreign_calendar__viewing', { ns: 'events', name: shownUserName })}
                className="border-brand/25 bg-brand/(--opacity-wash) hover:border-brand/50"
            >
                <ValueChipBody
                    onClick={() => {
                        setPickerOpen(true);
                    }}
                >
                    <UserCircleIcon className="text-brand!" />
                    <ValueChipLabel />
                </ValueChipBody>
                <ValueChipRemove
                    label={t('foreign_calendar__back_to_own', { ns: 'events' })}
                    onClick={backToOwnCalendar}
                />
            </ValueChip>
        ) : (
            <ToolbarButton
                onClick={() => {
                    setPickerOpen(true);
                }}
            >
                <UserCircleIcon className="size-4" />
                {t('foreign_calendar', { ns: 'events' })}
            </ToolbarButton>
        ));

    return (
        <div className="@container pt-page">
            <div className="flex flex-wrap items-center gap-cozy">
                {/* Desktop title: month and KW side by side */}
                <div className="hidden shrink-0 items-center gap-cozy @[40rem]:flex">
                    <CalendarIcon className="size-4" />
                    <CalendarDatePicker
                        day={date}
                        viewMode={viewMode}
                        disableWeekends={skipWeekends}
                        onPick={pickDate}
                        className={cn(
                            '-ml-tight flex cursor-pointer items-center gap-tight rounded-default px-tight',
                            'transition duration-state ease-state hover:bg-content/(--opacity-wash)',
                        )}
                    >
                        {/* Spans, not Typography: a heading can't sit inside a button */}
                        <span className="text-heading-1">{monthTitle}</span>
                        <span className="ml-tight text-heading-3 font-normal text-content-weaker">
                            {weekLabel}
                        </span>
                        <ChevronDownIcon className="size-4 text-content-weak" />
                    </CalendarDatePicker>
                </div>
                {/* Mobile: avatar opens the picker for your own calendar, and goes back to it from someone else's */}
                {canViewForeignCalendar && (
                    <button
                        type="button"
                        aria-label={
                            isForeignCalendar
                                ? t('foreign_calendar__back_to_own', { ns: 'events' })
                                : t('foreign_calendar', { ns: 'events' })
                        }
                        onClick={() => {
                            if (isForeignCalendar) {
                                backToOwnCalendar();
                            } else {
                                setPickerOpen(true);
                            }
                        }}
                        className="relative -ml-0.5 flex size-11 shrink-0 cursor-pointer items-center justify-center select-none active:translate-px @[40rem]:hidden"
                    >
                        <Avatar variant={isForeignCalendar ? 'primary' : 'secondary'} className="size-10">
                            {shownUser ? getInitials(shownUser.firstName, shownUser.lastName) : ''}
                        </Avatar>
                        <span className="absolute -right-0.5 -bottom-0.5 flex size-4 items-center justify-center rounded-full border border-container bg-neutral-100">
                            {isForeignCalendar ? (
                                <XIcon className="size-3 text-content-mid" />
                            ) : (
                                <ChevronDownIcon className="size-3 text-content-mid" />
                            )}
                        </span>
                    </button>
                )}
                {/* Mobile title: KW as a caption under the month, back-to-today beside it while in another week */}
                <div className="flex min-w-0 flex-1 items-center gap-cozy @[40rem]:hidden">
                    {/* Phones are always in day view, so this picks a day there */}
                    <CalendarDatePicker
                        day={date}
                        viewMode={viewMode}
                        disableWeekends={skipWeekends}
                        onPick={pickDate}
                        className="flex min-w-0 cursor-pointer flex-col items-start text-left"
                    >
                        <span className="flex max-w-full items-center gap-tight">
                            <span className="truncate text-heading-1">{monthTitle}</span>
                            <ChevronDownIcon className="size-4 shrink-0 text-content-weak" />
                        </span>
                        <span className="text-body text-content-weaker">{weekLabel}</span>
                    </CalendarDatePicker>
                    {!isCurrentWeek && (
                        <ToolbarButton
                            aria-label={t('today', { ns: 'events' })}
                            className="size-9 px-0"
                            onClick={goToToday}
                        >
                            {t('today_day_number', {
                                ns: 'events',
                                count: new Date().getDate(),
                                ordinal: true,
                            })}
                        </ToolbarButton>
                    )}
                </div>
                {/* Mobile: icon-only maps */}
                <IconButton
                    variant="secondary"
                    label={t('open_route_in_maps', { ns: 'events' })}
                    className="size-11 @[40rem]:hidden"
                    {...mapsLinkProps}
                >
                    <MapIcon className="size-5" />
                </IconButton>
                {/* Mobile: icon-only create */}
                <IconButton
                    variant="primary"
                    label={t('page_header__new_event', { ns: 'events' })}
                    className="size-11 @[40rem]:hidden"
                    onClick={openCreateEvent}
                    disabled={!canCreateEvent}
                >
                    <PlusIcon className="size-5" />
                </IconButton>
                {/* View toggle + paging: not on phones, which only have the day view and page with the day strip */}
                <div className="flex w-full items-center justify-between gap-cozy max-sm:hidden @[40rem]:w-auto">
                    <ToggleGroup
                        selected={viewMode}
                        onSelectedChange={(value) => {
                            if (value === 'day' || value === 'week') {
                                onViewModeChange(value);
                            }
                        }}
                    >
                        <InsetToggleButton optionValue="day">
                            <CalendarIcon />
                            {t('period__day', { ns: 'events' })}
                        </InsetToggleButton>
                        <InsetToggleButton optionValue="week">
                            <CalendarDaysIcon />
                            {t('period__week', { ns: 'events' })}
                        </InsetToggleButton>
                    </ToggleGroup>
                    <div className="flex items-center gap-tight">
                        <Button
                            variant="secondary"
                            className="hidden @[40rem]:inline-flex"
                            disabled={isCurrentPeriod}
                            onClick={goToToday}
                        >
                            {t('today', { ns: 'events' })}
                        </Button>
                        <IconButton
                            label={
                                viewMode === 'day'
                                    ? t('calendar_nav__previous_day', { ns: 'events' })
                                    : t('calendar_nav__previous_week', { ns: 'events' })
                            }
                            variant="secondary"
                            onClick={() => {
                                handleChange(previousDate, 'previous');
                            }}
                        >
                            <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                            label={
                                viewMode === 'day'
                                    ? t('calendar_nav__next_day', { ns: 'events' })
                                    : t('calendar_nav__next_week', { ns: 'events' })
                            }
                            variant="secondary"
                            onClick={() => {
                                handleChange(nextDate, 'next');
                            }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </div>
                </div>
                {/* Desktop actions: Fremder Kalender (below 60rem) and Maps (below 46rem) move to the row below */}
                <div className="ml-auto hidden items-center gap-default @[40rem]:flex">
                    <div className="hidden @[60rem]:flex">{foreignCalendarControl}</div>
                    {/* The route runs through one day's visits, so the week view has none to show */}
                    {viewMode === 'day' && (
                        <ToolbarButton
                            aria-label={t('open_route_in_maps', { ns: 'events' })}
                            className="hidden @[46rem]:inline-flex"
                            {...mapsLinkProps}
                        >
                            <MapIcon className="size-4" />
                            {t('maps', { ns: 'events' })}
                            <ArrowUpRightIcon className="size-4" />
                        </ToolbarButton>
                    )}
                    <Button variant="primary" onClick={openCreateEvent} disabled={!canCreateEvent}>
                        <PlusIcon className="size-4" />
                        {t('page_header__new_event', { ns: 'events' })}
                    </Button>
                </div>
                {/* Desktop second row for the actions that do not fit next to the title */}
                <div className="hidden w-full items-center justify-end gap-default @[40rem]:flex @[60rem]:hidden">
                    {foreignCalendarControl}
                    {viewMode === 'day' && (
                        <ToolbarButton
                            aria-label={t('open_route_in_maps', { ns: 'events' })}
                            className="@[46rem]:hidden"
                            {...mapsLinkProps}
                        >
                            <MapIcon className="size-4" />
                            {t('maps', { ns: 'events' })}
                            <ArrowUpRightIcon className="size-4" />
                        </ToolbarButton>
                    )}
                </div>
            </div>
            <ForeignCalendarPicker
                open={isPickerOpen}
                onOpenChange={setPickerOpen}
                colleagues={colleagues}
                selectedUserId={selectedOwnerId}
                onPick={pickColleague}
            />
        </div>
    );
}
